import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { PlatformService, UserService } from '@evc/platform';
import { filter, Subject, take, takeUntil } from 'rxjs';

import {
  CanvasCommunicationService,
  DialogNotification,
  FeatureFlagsConfigService,
  NetworkSettings,
  NetworkSettingsComponent,
  NotificationDialogComponent,
} from '@olympus/ngx-shared';
import { UNIS_UT_ONEUTVIEW_APP_NAME } from 'electron-app/app-names';
import { DialogService } from 'shared-modules-angular/dialog';
import { ModeSelectionService } from 'shared-modules-angular/feature-flags';

import { WindViewPreference } from './core/app.preference';
import { UtDesktopMode } from './core/config/feature-flags.config';
import { ConfigService } from './core/services/config';
import { SignalrService } from './core/services/signalr/signalr.service';
import { LicenseFacadeService } from './modules/license/services/license-facade.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    RouterOutlet,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _licenseService = inject(LicenseFacadeService);

  private readonly _preference = inject(WindViewPreference);
  private readonly _communicationService = inject(CanvasCommunicationService);
  private readonly _configService = inject(ConfigService);
  private readonly _dialogService = inject(DialogService);
  private readonly _signalrService = inject(SignalrService);
  private readonly _modeSelectionService = inject(ModeSelectionService<UtDesktopMode>);
  private readonly _featureFlagsConfigService = inject(FeatureFlagsConfigService);

  private readonly _evcPlatformService: PlatformService | null = null;
  private readonly _userService: UserService | null = null;

  private readonly _unsubscribe$ = new Subject<void>();

  constructor() {
    if (this._configService.isEvidentConnect && this._configService.isCloudConnected) {
      this._evcPlatformService = inject(PlatformService);
      this._userService = inject(UserService);
    }

    if (this._featureFlagsConfigService.appConfig.appName === UNIS_UT_ONEUTVIEW_APP_NAME) {
      this._modeSelectionService.selectMode('corrosion');
    }
  }

  public ngOnInit(): void {
    this._evcPlatformService?.init();
    this._userService?.init();

    this._licenseService.ensureLicensed().pipe(take(1)).subscribe(() => {
      this.initDpu();
    });
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
  }

  private initDpu(): void {
    if (this._configService.isCloudConnected) {
      this._signalrService.initConnection();
    } else {
      this._preference.get(NetworkSettingsComponent.PREFERENCE_KEY_DPU).pipe(
        takeUntil(this._unsubscribe$),
      ).subscribe((settings: NetworkSettings) => {
        this._communicationService.communication.disconnect();
        this._communicationService.communication.connect({
          ipAddress: settings.ipAddress,
          dpuId: '',
          commandPort: settings.portCommand,
          dataPort: settings.portData,
          eventPort: settings.portEvent,
          autoConnect: true,
          secure: false,
          useRoute: false,
          usePingPong: true,
        });
      });
    }

    this._communicationService.communication.maxConnectionReached$.pipe(
      filter(connection => !!connection),
      takeUntil(this._unsubscribe$),
    ).subscribe(() => {
      const notification: DialogNotification = {
        headerTitle: 'Connection Error',
        titleIconName: 'exclamation-triangle',
        bodyMessage: 'Connection refused: Maximum number of client reached!',
      };

      this._dialogService.open(NotificationDialogComponent, { data: notification });
    });
  }
}
